/**
 * ReviewCourses RPC 契约（workbench/review-courses 域，25 端点）。
 *
 * payload schema 复用 workbench 应用 domain 闭包
 * （apps/workbench/src/domain/review-course-inputs，monorepo 下应用层 SSOT）。
 *
 * 端点权限映射（实现层 requirePermissionsEffect 声明，与旧 serverFn
 * middleware access([...]) 一一对应）：
 * - 读端点（getReviewCourses / getReviewModules* / getReviewLessons* /
 *   getReviewResources / getCourseProgress* / getLessonProgress /
 *   saveLessonProgress / clearLessonProgress）
 *   → REVIEW_COURSE_READ_ALL
 * - 写端点（create/update/delete/batchReorder*）
 *   → REVIEW_COURSE_WRITE_ALL
 *
 * success schema：精确 Struct（行类型与 @crm/db/schema/fulfillment 的 Drizzle
 * $inferSelect 对齐；Date 字段 wire=ISO string，decode 回 Date）。
 */
import { Schema } from "effect";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import {
  batchReorderLessonsInputSchema,
  clearLessonProgressInputSchema,
  courseIdInputSchema,
  createReviewCourseInputSchema,
  createReviewLessonInputSchema,
  createReviewModuleInputSchema,
  createReviewResourceInputSchema,
  entityIdInputSchema,
  getCourseProgressBatchInputSchema,
  getCourseProgressInputSchema,
  getLessonProgressInputSchema,
  getReviewCoursesInputSchema,
  getReviewResourcesInputSchema,
  moduleIdInputSchema,
  saveLessonProgressInputSchema,
  undefinedInputSchema,
  updateReviewCourseInputSchema,
  updateReviewLessonInputSchema,
  updateReviewModuleInputSchema,
  updateReviewResourceInputSchema,
} from "../../../../apps/workbench/src/domain/review-course-inputs";
import { reviewResourceTypeEnum, reviewStatusEnum } from "@crm/db/schema/fulfillment";
import { asContractSchema, RpcAppError } from "../errors";
import { RpcAuthMiddleware } from "../middleware";

const ReviewStatusSchema = Schema.Literals([...reviewStatusEnum.enumValues]);
const ReviewResourceTypeSchema = Schema.Literals([...reviewResourceTypeEnum.enumValues]);

/** 复习课程行（精确契约：Date 字段 wire=ISO string，decode 回 Date） */
export const ReviewCourseSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  thumbnail: Schema.NullOr(Schema.String),
  courseId: Schema.NullOr(Schema.String),
  status: ReviewStatusSchema,
  order: Schema.Finite,
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type ReviewCourseWire = Schema.Schema.Type<typeof ReviewCourseSchema>;

/** 复习模块行 */
export const ReviewModuleSchema = Schema.Struct({
  id: Schema.String,
  courseId: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  order: Schema.Finite,
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type ReviewModuleWire = Schema.Schema.Type<typeof ReviewModuleSchema>;

/** 复习课时行 */
export const ReviewLessonSchema = Schema.Struct({
  id: Schema.String,
  moduleId: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  videoId: Schema.NullOr(Schema.String),
  duration: Schema.NullOr(Schema.Finite),
  status: ReviewStatusSchema,
  order: Schema.Finite,
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type ReviewLessonWire = Schema.Schema.Type<typeof ReviewLessonSchema>;

/** 复习资源行 */
export const ReviewResourceSchema = Schema.Struct({
  id: Schema.String,
  courseId: Schema.NullOr(Schema.String),
  moduleId: Schema.NullOr(Schema.String),
  lessonId: Schema.NullOr(Schema.String),
  title: Schema.String,
  description: Schema.NullOr(Schema.String),
  type: ReviewResourceTypeSchema,
  url: Schema.NullOr(Schema.String),
  fileSize: Schema.NullOr(Schema.Finite),
  order: Schema.Finite,
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type ReviewResourceWire = Schema.Schema.Type<typeof ReviewResourceSchema>;

/** 课时进度行（lessonProgress 表） */
export const LessonProgressSchema = Schema.Struct({
  id: Schema.String,
  userId: Schema.String,
  lessonId: Schema.String,
  watchedSeconds: Schema.Finite,
  lastPosition: Schema.Finite,
  progressPercentage: Schema.Finite,
  completed: Schema.Boolean,
  completedAt: Schema.NullOr(Schema.DateFromString),
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type LessonProgressWire = Schema.Schema.Type<typeof LessonProgressSchema>;

/** 课程进度概览项（SQL 聚合行） */
export const CourseProgressItemSchema = Schema.Struct({
  lessonId: Schema.String,
  completed: Schema.Boolean,
  progressPercentage: Schema.Finite,
  watchedSeconds: Schema.Finite,
});
export type CourseProgressItemWire = Schema.Schema.Type<typeof CourseProgressItemSchema>;

/** 批量课程进度概览项（含 courseId） */
export const BatchCourseProgressItemSchema = Schema.Struct({
  courseId: Schema.String,
  lessonId: Schema.String,
  completed: Schema.Boolean,
  progressPercentage: Schema.Finite,
  watchedSeconds: Schema.Finite,
});
export type BatchCourseProgressItemWire = Schema.Schema.Type<typeof BatchCourseProgressItemSchema>;

const DeleteResultSchema = Schema.Struct({
  success: Schema.Boolean,
  id: Schema.String,
});

const SuccessSchema = Schema.Struct({
  success: Schema.Boolean,
});

export class ReviewCoursesRpc extends RpcGroup.make(
  Rpc.make("getReviewCourses", {
    payload: asContractSchema(getReviewCoursesInputSchema),
    success: Schema.Array(ReviewCourseSchema),
    error: RpcAppError,
  }),
  Rpc.make("createReviewCourse", {
    payload: asContractSchema(createReviewCourseInputSchema),
    success: Schema.UndefinedOr(ReviewCourseSchema),
    error: RpcAppError,
  }),
  Rpc.make("updateReviewCourse", {
    payload: asContractSchema(updateReviewCourseInputSchema),
    success: Schema.UndefinedOr(ReviewCourseSchema),
    error: RpcAppError,
  }),
  Rpc.make("deleteReviewCourse", {
    payload: asContractSchema(entityIdInputSchema),
    success: DeleteResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("getReviewModules", {
    payload: asContractSchema(undefinedInputSchema),
    success: Schema.Array(ReviewModuleSchema),
    error: RpcAppError,
  }),
  Rpc.make("getReviewModulesByCourse", {
    payload: asContractSchema(courseIdInputSchema),
    success: Schema.Array(ReviewModuleSchema),
    error: RpcAppError,
  }),
  Rpc.make("createReviewModule", {
    payload: asContractSchema(createReviewModuleInputSchema),
    success: Schema.UndefinedOr(ReviewModuleSchema),
    error: RpcAppError,
  }),
  Rpc.make("updateReviewModule", {
    payload: asContractSchema(updateReviewModuleInputSchema),
    success: Schema.UndefinedOr(ReviewModuleSchema),
    error: RpcAppError,
  }),
  Rpc.make("deleteReviewModule", {
    payload: asContractSchema(entityIdInputSchema),
    success: DeleteResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("getReviewLessons", {
    payload: asContractSchema(undefinedInputSchema),
    success: Schema.Array(ReviewLessonSchema),
    error: RpcAppError,
  }),
  Rpc.make("getReviewLessonsByModule", {
    payload: asContractSchema(moduleIdInputSchema),
    success: Schema.Array(ReviewLessonSchema),
    error: RpcAppError,
  }),
  Rpc.make("getReviewLessonsByCourse", {
    payload: asContractSchema(courseIdInputSchema),
    success: Schema.Array(ReviewLessonSchema),
    error: RpcAppError,
  }),
  Rpc.make("createReviewLesson", {
    payload: asContractSchema(createReviewLessonInputSchema),
    success: Schema.UndefinedOr(ReviewLessonSchema),
    error: RpcAppError,
  }),
  Rpc.make("updateReviewLesson", {
    payload: asContractSchema(updateReviewLessonInputSchema),
    success: Schema.UndefinedOr(ReviewLessonSchema),
    error: RpcAppError,
  }),
  Rpc.make("deleteReviewLesson", {
    payload: asContractSchema(entityIdInputSchema),
    success: DeleteResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("batchReorderLessons", {
    payload: asContractSchema(batchReorderLessonsInputSchema),
    success: SuccessSchema,
    error: RpcAppError,
  }),
  Rpc.make("getReviewResources", {
    payload: asContractSchema(getReviewResourcesInputSchema),
    success: Schema.Array(ReviewResourceSchema),
    error: RpcAppError,
  }),
  Rpc.make("createReviewResource", {
    payload: asContractSchema(createReviewResourceInputSchema),
    success: ReviewResourceSchema,
    error: RpcAppError,
  }),
  Rpc.make("updateReviewResource", {
    payload: asContractSchema(updateReviewResourceInputSchema),
    success: ReviewResourceSchema,
    error: RpcAppError,
  }),
  Rpc.make("deleteReviewResource", {
    payload: asContractSchema(entityIdInputSchema),
    success: SuccessSchema,
    error: RpcAppError,
  }),
  Rpc.make("getLessonProgress", {
    payload: asContractSchema(getLessonProgressInputSchema),
    success: Schema.Array(LessonProgressSchema),
    error: RpcAppError,
  }),
  Rpc.make("saveLessonProgress", {
    payload: asContractSchema(saveLessonProgressInputSchema),
    success: LessonProgressSchema,
    error: RpcAppError,
  }),
  Rpc.make("clearLessonProgress", {
    payload: asContractSchema(clearLessonProgressInputSchema),
    success: SuccessSchema,
    error: RpcAppError,
  }),
  Rpc.make("getCourseProgress", {
    payload: asContractSchema(getCourseProgressInputSchema),
    success: Schema.Array(CourseProgressItemSchema),
    error: RpcAppError,
  }),
  Rpc.make("getCourseProgressBatch", {
    payload: asContractSchema(getCourseProgressBatchInputSchema),
    success: Schema.Array(BatchCourseProgressItemSchema),
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}
